// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Pie } from 'react-chartjs-2';
import { toast } from 'sonner';
import { EducBox, ChartBox, ResultCard, InputRow, EDUC, showToastOnce, fmt } from './shared';

const BUDGET_COLORS = ['#3D7A52', '#C9A84C', '#5B8DB8', '#B86B5B', '#8B7DB8', '#888780', '#1D9E75'];
const CUSTOM_COLORS = ['#5B8DB8', '#B86B5B', '#8B7DB8', '#D4845A'];

const BudgetCalc = ({ onStepComplete }) => {
  const navigate = useNavigate();
  const [income, setIncome] = useState('2500');
  const [alloggio, setAlloggio] = useState('800');
  const [cibo, setCibo] = useState('400');
  const [trasporti, setTrasporti] = useState('200');
  const [utenze, setUtenze] = useState('150');
  const [svago, setSvago] = useState('200');
  const [risparmio, setRisparmio] = useState('300');
  const [customRows, setCustomRows] = useState([]);

  const totalExpenses = [alloggio, cibo, trasporti, utenze, svago, risparmio]
    .reduce((s, v) => s + (parseFloat(v) || 0), 0)
    + customRows.reduce((s, r) => s + (parseFloat(r.value) || 0), 0);
  const inc = parseFloat(income) || 0;
  const savings = inc - totalExpenses;

  useEffect(() => {
    if (savings > 0) {
      showToastOnce('budget', () => {
        toast('Ottimo!', {
          description: `Risparmi €${fmt(savings)}/mese. Scopri quanto diventano in 20 anni →`,
          action: { label: 'Simula ora', onClick: () => navigate(`/calcolatori/pac?importo=${Math.round(savings)}`) },
          duration: 5000,
        });
      });
      if (onStepComplete) onStepComplete();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savings > 0]);

  const addRow = () => setCustomRows(r => [...r, { label: `Voce ${r.length + 1}`, value: '0' }]);
  const updateRow = (i, field, val) => setCustomRows(r => r.map((row, idx) => idx === i ? { ...row, [field]: val } : row));
  const removeRow = (i) => setCustomRows(r => r.filter((_, idx) => idx !== i));

  const categories = [
    { label: 'Alloggio', value: parseFloat(alloggio) || 0 },
    { label: 'Cibo', value: parseFloat(cibo) || 0 },
    { label: 'Trasporti', value: parseFloat(trasporti) || 0 },
    { label: 'Utenze', value: parseFloat(utenze) || 0 },
    { label: 'Svago', value: parseFloat(svago) || 0 },
    { label: 'Risparmio', value: parseFloat(risparmio) || 0 },
    ...customRows.map((r) => ({ label: r.label, value: parseFloat(r.value) || 0 })),
  ].filter(c => c.value > 0);

  const allColors = [...BUDGET_COLORS, ...CUSTOM_COLORS];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <EducBox text={EDUC.budget} />
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <InputRow label="Entrate mensili nette (€)" value={income} onChange={setIncome} prefix="€" />
        <div />
        <InputRow label="Alloggio (affitto/mutuo)" value={alloggio} onChange={setAlloggio} prefix="€" />
        <InputRow label="Cibo e spesa" value={cibo} onChange={setCibo} prefix="€" />
        <InputRow label="Trasporti" value={trasporti} onChange={setTrasporti} prefix="€" />
        <InputRow label="Utenze" value={utenze} onChange={setUtenze} prefix="€" />
        <InputRow label="Svago e uscite" value={svago} onChange={setSvago} prefix="€" />
        <InputRow label="Risparmio" value={risparmio} onChange={setRisparmio} prefix="€" />
        {customRows.map((row, i) => (
          <div key={i} className="flex gap-2">
            <div className="flex-1">
              <InputRow label={row.label} value={row.value} onChange={v => updateRow(i, 'value', v)} prefix="€" />
            </div>
            <button onClick={() => removeRow(i)} className="mt-6 text-red-400 hover:text-red-300 text-sm px-2">✕</button>
          </div>
        ))}
      </div>
      <button onClick={addRow} className="text-sm text-[#C9A84C] hover:text-[#E8E8E4] mb-6 transition-colors">+ Aggiungi voce</button>
      <div className="grid grid-cols-3 gap-4 mb-6">
        <ResultCard label="Entrate" value={`€${fmt(inc)}`} />
        <ResultCard label="Uscite totali" value={`€${fmt(totalExpenses)}`} />
        <ResultCard label="Risparmio netto" value={`€${fmt(savings)}`} green={savings >= 0} gold={savings < 0} />
      </div>
      {categories.length > 0 && (
        <ChartBox title="Distribuzione spese mensili" height={360}>
          <Pie
            data={{
              labels: categories.map(c => c.label),
              datasets: [{ data: categories.map(c => c.value), backgroundColor: allColors.slice(0, categories.length), borderWidth: 0 }],
            }}
            options={{
              responsive: true, maintainAspectRatio: false,
              plugins: {
                legend: { display: true, position: 'right', labels: { color: '#888780', usePointStyle: true, pointStyleWidth: 10 } },
                tooltip: { callbacks: { label: (ctx) => `${ctx.label}: €${fmt(ctx.parsed)}` } },
              },
            }}
          />
        </ChartBox>
      )}
</motion.div>
  );
};

export default BudgetCalc;
